import { Component, OnInit, Input, ChangeDetectionStrategy, OnDestroy, ChangeDetectorRef, AfterViewInit } from '@angular/core';
import { FavoriteService, AnalyticsService,  AuthService, User, FeatureFlagService } from '@icullen/api/shared';
import { OurTeam } from '@icullen/api/document';
import { Router, ActivatedRoute, Data } from '@angular/router';
import { AuthorDetail } from './document.model';
import { ActionIconsData } from '../action-icons/action-icons.model';
import { RelatedDocument } from '../related-posts/related-doc/related-doc.model';
import { UpgradeSubscriptionInfo } from '../demo/subscribe/upgrade-subscription-info.model';
import { Section } from '../section/section.model';
import { Subscription } from 'rxjs/internal/Subscription';
import { Observable } from 'rxjs';
import { finalize, first, map } from 'rxjs/operators';
import { TopicsService } from '@icullen/api/site';
import { ViewportScroller } from '@angular/common';
@Component({
  selector: 'document',
  template: '<div>no template defined</div>',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() docData: any;
  @Input() authorDetail: AuthorDetail;
  @Input() similarDocs: RelatedDocument[];
  private sub: Subscription;

  isFavoriteDoc = false;
  mailtoAddress: string;

  currentUser: User;
  showForgotPassword = false;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private favoriteService: FavoriteService,
    private analyticsService: AnalyticsService,
    private authService: AuthService,
    private topicsService: TopicsService,
    private cdr: ChangeDetectorRef,
    protected featureFlagService: FeatureFlagService,
    private viewportScroller: ViewportScroller
  ) {}

  ngOnInit() {
    this.isFavoriteDoc = this.favoriteService.isFavorite(
      this.docData.uniqueNumber
    );
    this.sub = this.authService.currentUserSubject.subscribe((user) => {
      this.currentUser = user;
    });
    if (this.docData.documentType.name === 'DMA_CASE') {
      this.displayDmaCase();
    }
    this.activatedRoute.fragment.subscribe(fragment => {
      if (fragment) {
        this.scrollToSection(fragment);
      }
    });
  }

  ngAfterViewInit() {
    const fragment = this.activatedRoute.snapshot.fragment;
    if (fragment) {
      setTimeout(() => {
        this.scrollToSection(fragment);
      }, 100);
    }
  }

  private scrollToSection(sectionUuid: string): void {
    // Méthode 1 : Utiliser ViewportScroller (recommandé)
    this.viewportScroller.scrollToAnchor(sectionUuid);
    
    // Méthode 2 : Alternative avec getElementById si la méthode 1 ne fonctionne pas
    // const element = document.getElementById(sectionUuid);
    // if (element) {
    //   element.scrollIntoView({ 
    //     behavior: 'smooth', 
    //     block: 'start' 
    //   });
    // }
  }

  getPublicationLink() {
    let publicationLink = '../' + this.docData.containingPublication;
    if (this.activatedRoute.snapshot.data.context === 'global') {
      publicationLink = '/global';
    }
    return publicationLink;
  }

  // getSectionsByTabName(tabName: string) {
  //   return this.docData.sections.filter(
  //     (section) => section.tabName === tabName
  //   );
  // }

  getSectionsByTabName(tabName: string) {
    const includedTypeNames = [
      'executiveSummary',
      'cl_fines',
      'cl_judgment',
      'cl_procedure',
      'cl_related_cases'
    ];
    return this.docData.sections.filter((section) => {
      if (this.docData.documentType.name === 'DMA_CASE') {
        return section.tabName === tabName && includedTypeNames.includes(section.typeName);
      } else {
        return section.tabName === tabName;
      }
    });
  }

  getEmphasisSectionsByTabName(tabName: string) {
    return this.docData.sections.filter(
      (section) => section.tabName === tabName && section.emphasis
    );
  }

  getDmaEmphasisSectionsByTypeName(tabName: string) {
    if (this.docData.documentType.name === 'DMA_CASE') {
      const invalidTypeNames = [
        'executiveSummary',
        'cl_fines',
        'cl_judgment',
        'cl_procedure',
        'cl_related_cases',
        'dma_sub_theme',
        'dma_core_platform',
        'dma_cps_commerical_name'
      ];
      const filteredSections = this.docData.sections.filter(
        (section) =>
          !invalidTypeNames.includes(section.typeName) && section.tabName === tabName
      );
      filteredSections.forEach((section) => {
        if (section.typeName === 'dma_authorities' || section.typeName === 'cl_reference' || section.typeName === 'dma_outcome' || section.typeName === 'dma_outcome_text') {
          section.alignment = 'left';
        } else {
          section.alignment = 'right';
        }
        if (section.answer && section.answer.includes('(CPS)')) {
          section.answer = section.answer.replace(/\(CPS\)/g, '').trim();
        }
      });
      const order = {
        dma_authorities: 1,
        legalBasis: 2,
        dma_outcome: 3,
        dma_outcome_text: 4,
        dma_core_platform: 5
      };
      filteredSections.sort((a, b) => (order[a.typeName] || 999) - (order[b.typeName] || 999));
      return filteredSections;
    }
  }
  
  addButtonClickAnalyticsData(event: string) {
    this.analyticsService.pushDocActionIconClickData(
      event,
      this.router.url,
      this.docData
    );
  }

  addTableAnalyticsData(clickedTab: any) {
    this.analyticsService.pushTableViewData(
      this.docData,
      this.router.url,
      clickedTab.tabName
    );
  }

  onFavoriteChanged(newState: boolean) {
    this.isFavoriteDoc = newState;
  }

  getOtherVersionDisplayClass(uniqueNumber: string): string {
    if (this.docData.uniqueNumber === uniqueNumber) {
      return 'Selected';
    }
    return '';
  }

  showLogin(): boolean {
    return this.currentUser == null || this.currentUser.partialAuth;
  }

  login() {
    let redirectUrl = this.router.url.split('?')[0];
    this.authService.logoutWithRedirectUrl(redirectUrl);
  }

  updateForgotPassword(event) {
    this.showForgotPassword = event;
  }

  getActionIconsData(separateQuestionIcon: boolean = false): ActionIconsData {
    return {
      docId: this.docData?.docId,
      isFavoriteDoc: this.favoriteService.isFavorite(this.docData.uniqueNumber),
      contactEmail: this.authorDetail?.contactEmail,
      uniqueNumber: this.docData?.uniqueNumber,
      separateQuestionIcon: separateQuestionIcon,
      canCorrectDocument:
        this.currentUser?.UserType === 'CULLEN_USER' &&
        !this.currentUser?.partialAuth,
      publication: this.docData?.publication,
      documentType: this.docData?.documentType.name,
      accessType: this.docData?.accessType,
    };
  }

  showTabs(): boolean {
    let tabLength: number =
      (this.showOtherVersions() ? 1 : 0) +
      this.docData.tabAnchors.tabAnchorList?.length;
    if (
      this.docData.publication && !this.docData.cullenPublicationDto?.tableOfContentDto?.execSummaryInToc
    ) {
      tabLength += 1;
    }
    return tabLength > 1;
  }

  showOtherVersions(): boolean {
    if (this.docData.publication) {
      return (
        this.docData.cullenPublicationDto?.tableOfContentDto
          ?.showOtherVersionsTab && this.docData.versions.length > 1
      );
    } else {
      return this.docData.versions.length > 1;
    }
  }

  getInfrigements(): Section {
    return this.docData.sections.filter(
      (section) => section.typeName === 'cl_infringements'
    )[0];
  }

  getDmaSubthemes(): Section {
    return this.docData.sections.filter(
      (section) => section.typeName === 'dma_sub_theme'
    )[0];
  }
  
  getDmaCps(): Section {
    return this.docData.sections.filter(
      (section) => section.typeName === 'dma_core_platform'
    )[0];
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  getAim(name: string): Section {
    return this.docData.sections.filter(
      (section) => section.typeName === 'sa_aim'
    )[0];
  }

  getTarget(name: string): Section {
    return this.docData.sections.filter(
      (section) => section.typeName === 'sa_target'
    )[0];
  }

  getAttachmentsTitle(length: number) {
    if (length === 1) {
      return 'attachment';
    } else if (length > 1) {
      return 'attachments';
    }
  }

  shouldShowDatabaseButton(docData: any): boolean {
    const supportedDocumentTypes = ['COMPETITION_LAW_DOC', 'STATE_AID', 'DMA_CASE'];
    return supportedDocumentTypes.includes(
      docData.documentType.documentDefinitionType
    );
  }
  
  // shouldShowSectionBasedToc(docData: any): boolean {
  //   const supportedDocumentTypes = ['BIG5', 'REGULATORYREPORT', 'FLASH'];
  //   return (
  //     supportedDocumentTypes.includes(docData.documentType.documentDefinitionType) &&
  //     docData.tabAnchors &&
  //     docData.tabAnchors.tabAnchorList &&
  //     docData.tabAnchors.tabAnchorList.length === 1 &&
  //     docData.sections.length > 1
  //   );
  // }

  shouldShowSectionBasedToc(docData: any): boolean {
    const supportedDocumentTypes = ['BIG5', 'BIG5_SECTION', 'REGULATORYREPORT', 'FLASH'];
    const allTitlesNull = docData.sections.every(
      (section: any) => section.title === null
    );
    return (
      supportedDocumentTypes.includes(
        docData.documentType.documentDefinitionType
      ) &&
      docData.tabAnchors &&
      docData.tabAnchors.tabAnchorList &&
      docData.tabAnchors.tabAnchorList.length === 1 &&
      docData.sections.length > 1 &&
      !allTitlesNull
    );
  }

  shouldShowCaseHeader(i: number, docData: any): boolean {
    const supportedDocumentTypes = ['COMPETITION_LAW_DOC', 'STATE_AID', 'DMA_CASE'];
    return (
      i === 0 &&
      supportedDocumentTypes.includes(
        docData.documentType.documentDefinitionType
      )
    );
  }

  displayDmaCase() {
    // SECTIONS
    const includedTypeNames = [
      'executiveSummary',
      'cl_fines',
      'cl_judgment',
      'cl_procedure',
      'cl_related_cases'
    ];
    let hasOutcomeSection = false;
    let mergedOutcomeAnswers = '';
    let hasAuthorityAndReferenceSection = false;
    let mergedAuthorityAndReference = '';
    this.docData.sections.forEach((section) => {
      section.tabName =
        section.typeName === 'cl_judgment' ? 'Details' : 'At a glance';
      if (
        section.tabName === 'At a glance' &&
        includedTypeNames.includes(section.typeName)
      ) {
        section.uuid = 'Merged';
      }
      if (section.typeName === 'dma_outcome' || section.typeName === 'dma_outcome_text') {
        hasOutcomeSection = true;
        if (section.answer) {
          mergedOutcomeAnswers += section.answer.trim() + ' ';
        }
      }
      if (section.typeName === 'dma_authorities' || section.typeName === 'cl_reference') {
        hasAuthorityAndReferenceSection = true;
        let transformedAnswer = section.answer.trim();
        if (section.typeName === 'dma_authorities') {
          transformedAnswer = transformedAnswer
            .replace(/<ul>/g, '')  // Supprime l'ouverture <ul>
            .replace(/<\/ul>/g, '') // Supprime la fermeture </ul>
            .replace(/<li[^>]*>(.*?)<\/li>/gs, '<p><b>$1</b></p>'); // Convertit <li> en <p><b>...</b></p>
        }
        mergedAuthorityAndReference += transformedAnswer + ' ';
      }
    });
    this.docData.sections = this.docData.sections.filter(
      (section) =>
        section.typeName !== 'dma_outcome' && section.typeName !== 'dma_outcome_text' && section.typeName !== 'dma_authorities' && section.typeName !== 'cl_reference'
    );
    if (hasOutcomeSection) {
      const newOutcomeSection = {
        name: 'outcome',
        title: '',
        uuid: 'merged',
        answer: mergedOutcomeAnswers.trim(),
        alignment: 'left',
        containerStyle: null,
        newLine: false,
        relatedDocuments: null,
        tabName: 'At a glance',
        sectionDtoType: 'LINEAR',
        miniAnswer: null,
        tableViewHeaders: null,
        pillarDtos: [
          {
            name: null,
            code: '',
            visible: false
          }
        ],
        countryCode: null,
        emphasis: true,
        topics: [],
        typeName: 'dma_outcome'
      };
      this.docData.sections.push(newOutcomeSection);
    }
    if (hasAuthorityAndReferenceSection) {
      const newAuthoritySection = {
        name: 'Authorities',
        title: '',
        uuid: 'merged',
        answer: mergedAuthorityAndReference.trim(),
        alignment: 'left',
        containerStyle: null,
        newLine: false,
        relatedDocuments: null,
        tabName: 'At a glance',
        sectionDtoType: 'LINEAR',
        miniAnswer: null,
        tableViewHeaders: null,
        pillarDtos: [
          {
            name: null,
            code: '',
            visible: false
          }
        ],
        countryCode: null,
        emphasis: true,
        topics: [],
        typeName: 'dma_authorities'
      };
      this.docData.sections.push(newAuthoritySection);
    }
    // TABANCHORLIST
    this.docData.tabAnchors.tabAnchorList = this.docData.tabAnchors.tabAnchorList.map((tab) => ({
      ...tab,
      tabName: 'At a glance',
      uid: 'Merged',
      indexes: Array.from({ length: this.docData.sections.length }, (_, i) => i),
      sectionDtoType: 'LINEAR',
      tableTab: false
    }));
    const clJudgmentSection = this.docData.sections.find(
      (section: { typeName: string; uuid: string }) =>
        section.typeName === 'cl_judgment'
    );
    if (clJudgmentSection) {
      const lastIndex = this.docData.sections.length - 1;
      this.docData.tabAnchors.tabAnchorList.push({
        tabName: 'Details',
        uid: clJudgmentSection.uuid,
        indexes: [lastIndex],
        sectionDtoType: 'LINEAR',
        tableTab: false
      });
    }
  }
}
